import { useRef, useState, useEffect } from "react";
import { Checkbox, Panel, DefaultButton, TextField, SpinButton } from "@fluentui/react";
import { Search24Regular, Lightbulb24Regular, Dismiss24Regular } from "@fluentui/react-icons";
import { ActionButton } from "../../components/ActionButton";

import styles from "./Chat.module.css";

import { chatApiGpt, Approaches, AskResponse, ChatRequestGpt, ChatTurn } from "../../api";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { UserChatMessage } from "../../components/UserChatMessage";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { ClearChatButton } from "../../components/ClearChatButton";
import { SummaryButton } from "../../components/SummaryButton";
import { SelectedSourcesModal } from "../../components/SelectedSourcesModal";
import { SamplePrompts } from "../../components/SamplePrompts";
import { getTokenOrRefresh } from "../../components/QuestionInput/token_util";
import { SpeechConfig, AudioConfig, SpeechSynthesizer, ResultReason } from "microsoft-cognitiveservices-speech-sdk";
import { getFileType } from "../../utils/functions";

const userLanguage = navigator.language;
let error_message_text = "";
if (userLanguage.startsWith("pt")) {
    error_message_text = "Desculpe, tive um problema técnico com a solicitação. Por favor informar o erro a equipe de suporte. ";
} else if (userLanguage.startsWith("es")) {
    error_message_text = "Lo siento, yo tuve un problema con la solicitud. Por favor informe el error al equipo de soporte. ";
} else {
    error_message_text = "I'm sorry, I had a problem with the request. Please report the error to the support team. ";
}

const Chat = () => {
    // speech synthesis is disabled by default
    const speechSynthesisEnabled = false;

    // Detectar variante desde URL params
    const [variant, setVariant] = useState<boolean>(false);
    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 1200);
    const [selectedSourcesModalMode, setSelectedSourcesModalMode] = useState<'manage' | 'search'>('manage');
    const [showAnalysisModal, setShowAnalysisModal] = useState<boolean>(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        setVariant(params.get('variant') === 'true');

        const handleResize = () => {
            setIsMobile(window.innerWidth < 1200);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [placeholderText, setPlaceholderText] = useState("");
    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [promptTemplate, setPromptTemplate] = useState<string>("");
    const [retrieveCount, setRetrieveCount] = useState<number>(3);
    const [useSemanticRanker, setUseSemanticRanker] = useState<boolean>(true);
    const [useSemanticCaptions, setUseSemanticCaptions] = useState<boolean>(false);
    const [excludeCategory, setExcludeCategory] = useState<string>("");
    const [useSuggestFollowupQuestions, setUseSuggestFollowupQuestions] = useState<boolean>(false);

    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const [fileType, setFileType] = useState<string>("");

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();

    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);

    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
    const [answers, setAnswers] = useState<[user: string, response: AskResponse][]>([]);

    const [userId, setUserId] = useState<string>("");
    const triggered = useRef(false);
    const [isSelectedSourcesModalOpen, setIsSelectedSourcesModalOpen] = useState<boolean>(false);
    const [isRightSidebarOpen, setIsRightSidebarOpen] = useState<boolean>(false);
    const [selectedSources, setSelectedSources] = useState<string[]>([]);
    const [selectedSourcesCount, setSelectedSourcesCount] = useState<number>(0);
    const [sources, setSources] = useState<any[]>([]);

    const makeApiRequestGpt = async (question: string) => {
        lastQuestionRef.current = question;

        error && setError(undefined);
        setIsLoading(true);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);

        try {
            const history: ChatTurn[] = answers.map(a => ({ user: a[0], bot: a[1].answer }));
            const request: ChatRequestGpt = {
                history: [...history, { user: question, bot: undefined }],
                approach: Approaches.ReadRetrieveRead,
                conversation_id: userId,
                query: question,
                overrides: {
                    promptTemplate: promptTemplate.length === 0 ? undefined : promptTemplate,
                    excludeCategory: excludeCategory.length === 0 ? undefined : excludeCategory,
                    top: retrieveCount,
                    semanticRanker: useSemanticRanker,
                    semanticCaptions: useSemanticCaptions,
                    suggestFollowupQuestions: useSuggestFollowupQuestions,
                    selectedSources: selectedSources.length > 0 ? selectedSources : undefined
                }
            };
            console.log("Sending request with selected sources:", selectedSources);
            const result = await chatApiGpt(request);
            console.log(result);
            console.log(result.answer);
            setAnswers([...answers, [question, result]]);
            setUserId(result.conversation_id);

            // Voice Synthesis
            if (speechSynthesisEnabled) {
                const tokenObj = await getTokenOrRefresh();
                const speechConfig = SpeechConfig.fromAuthorizationToken(tokenObj.authToken, tokenObj.region);
                const audioConfig = AudioConfig.fromDefaultSpeakerOutput();
                speechConfig.speechSynthesisLanguage = tokenObj.speechSynthesisLanguage;
                speechConfig.speechSynthesisVoiceName = tokenObj.speechSynthesisVoiceName;
                const synthesizer = new SpeechSynthesizer(speechConfig, audioConfig);

                synthesizer.speakTextAsync(
                    result.answer.replace(/ *\[[^)]*\] */g, ""),
                    function (result) {
                        if (result.reason === ResultReason.SynthesizingAudioCompleted) {
                            console.log("synthesis finished.");
                        } else {
                            console.error("Speech synthesis canceled, " + result.errorDetails + "\nDid you update the subscription info?");
                        }
                        synthesizer.close();
                    },
                    function (err) {
                        console.trace("err - " + err);
                        synthesizer.close();
                    }
                );
            }
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    const clearChat = () => {
        console.log("file is" + fileType);
        lastQuestionRef.current = "";
        error && setError(undefined);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setAnswers([]);
        setUserId("");


        setActiveAnalysisPanelTab(undefined);
        setIsRightSidebarOpen(false);


    };

    const requestSummary = () => {
        makeApiRequestGpt("Puedes realizar un resumen de los documentos??");
    };

    /**Get Pdf */
    const getPdf = async (pdfName: string) => {
        /** get file type */
        let type = getFileType(pdfName);
        setFileType(type);

        try {
            const response = await fetch("/api/get-blob", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    blob_name: pdfName
                })
            });

            if (!response.ok) {
                throw new Error(`Error fetching DOC: ${response.status}`);
            }

            return await response.blob();
        } catch (error) {
            console.error(error);
            throw new Error("Error fetching DOC.");
        }
    };

    useEffect(() => {
        if (triggered.current === false) {
            triggered.current = true;
        }
        const language = navigator.language;
        const isSamplePromptsVisible = !lastQuestionRef.current && selectedSourcesCount === 0;

        if (isSamplePromptsVisible) {
            if (language.startsWith("pt")) {
                setPlaceholderText("Ej: Que concluiu o estudo do PMTI?");
            } else if (language.startsWith("es")) {
                setPlaceholderText("Ej: ¿Qué concluyó el estudio del PMTI?");
            } else {
                setPlaceholderText("Ej: What did the PMTI study conclude?");
            }
        } else {
            if (language.startsWith("pt")) {
                setPlaceholderText("Escreva sua pergunta aqui");
            } else if (language.startsWith("es")) {
                setPlaceholderText("Escribe acá tu pregunta");
            } else {
                setPlaceholderText("Write your question here");
            }
        }
    }, [isLoading, selectedSourcesCount, answers]);

    const onPromptTemplateChange = (_ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setPromptTemplate(newValue || "");
    };

    const onRetrieveCountChange = (_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setRetrieveCount(parseInt(newValue || "3"));
    };

    const onUseSemanticRankerChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSemanticRanker(!!checked);
    };

    const onUseSemanticCaptionsChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSemanticCaptions(!!checked);
    };

    const onExcludeCategoryChanged = (_ev?: React.FormEvent, newValue?: string) => {
        setExcludeCategory(newValue || "");
    };

    const onUseSuggestFollowupQuestionsChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSuggestFollowupQuestions(!!checked);
    };

    const onExampleClicked = (example: string) => {
        makeApiRequestGpt(example);
    };

    const onShowCitation = async (citation: string, fileName: string, index: number) => {
        setActiveAnalysisPanelTab(AnalysisPanelTabs.ThoughtProcessTab);
        setActiveCitation(citation);
        setShowAnalysisModal(true);
        setSelectedAnswer(index);
        console.log("Citation clicked: ", citation, fileName, index);
        console.log("ismobile:", isMobile);
        console.log("isvariant:", variant);
        console.log("file is" + fileType);
        console.log("activeAnalysisPanelTab:", activeAnalysisPanelTab);
        console.log("answers.length:", answers.length);
        console.log("isrightSidebarOpen:", isRightSidebarOpen);
        console.log("activeCitation:", activeCitation);
        console.log("selectedAnswer:", selectedAnswer);
        // onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)
    };

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        setActiveAnalysisPanelTab(tab);
        setShowAnalysisModal(true);
        setSelectedAnswer(index);
    };

    return (
        <div className={styles.container}>
            <div className={`${styles.chatRoot} ${variant ? styles.variantMode : ''}`}>
                {/* SelectedSourcesModal ahora es un Modal siempre visible si isSelectedSourcesModalOpen es true */}
                <SelectedSourcesModal
                    isOpen={isSelectedSourcesModalOpen}
                    onToggle={() => setIsSelectedSourcesModalOpen(false)}
                    onSelectedSourcesChange={setSelectedSources}
                    onSelectedSourcesCountChange={setSelectedSourcesCount}
                    sources={sources}
                    onSourcesChange={setSources}
                    mode={selectedSourcesModalMode}
                />

                <div className={styles.chatMainContent}>
                    <div className={styles.chatContainer}>
                        {(!variant || lastQuestionRef.current) && (
                            <div className={`${styles.commandsContainer} ${variant ? styles.commandsContainerVariant : ""}`}>
                                <div className={`${styles.commandsTopRow} ${variant ? styles.commandsTopRowVariant : ""}`}>
                                    {/* Botón para abrir Publicaciones/Estudios siempre visible */}
                                    {!variant && (
                                        <ActionButton
                                            variant="primary"
                                            title="Publicaciones/Estudios"
                                            onClick={() => {
                                                setSelectedSourcesModalMode('search');
                                                setIsSelectedSourcesModalOpen(true);
                                            }}
                                            className={`${styles.mobileSourcesButton} ${styles.topRowControlButton}`}
                                            icon={<Search24Regular />}
                                        >
                                            Buscar/Seleccionar Publicaciones
                                        </ActionButton>
                                    )}

                                    {/* Botón para reiniciar chat (oculto si no hay conversación) */}
                                    {lastQuestionRef.current && (
                                        <ClearChatButton
                                            className={`${styles.commandButton} ${styles.topRowControlButton}`}
                                            onClick={clearChat}
                                            disabled={isLoading}
                                            linkMode={variant}
                                        />
                                    )}
                                </div>

                                <div className={styles.commandsBottomRow}>
                                    {!variant && (
                                        <div className={styles.sourcesInfo}>
                                            {selectedSourcesCount === 0 ? (
                                                <span>Interactuando con todas las publicaciones</span>
                                            ) : (
                                                <span>Interactuando con <span className={styles.highlightedCount}>{selectedSourcesCount}</span> publicaci{selectedSourcesCount === 1 ? 'ón' : 'ones'}</span>
                                            )}
                                        </div>
                                    )}
                                    {!variant && selectedSourcesCount > 0 && (
                                        <div className={styles.sourcesInfoRow}>
                                            <SummaryButton
                                                onClick={() => {
                                                    setSelectedSourcesModalMode('manage');
                                                    setIsSelectedSourcesModalOpen(true);
                                                }}
                                                textOverride="Ver/remover publicaciones seleccionadas"
                                                linkMode={true}
                                            />
                                            <div className={styles.separatorLine}></div>
                                            <SummaryButton
                                                onClick={requestSummary}
                                                disabled={isLoading}
                                                textOverride="Resumir documentos asociados"
                                                linkMode={true}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {!lastQuestionRef.current && (
                            <div className={styles.chatEmptyState}>
                                {/* <div className={styles.appNameContainer}>
                                    <div className={styles.appNameIcon}>
                                        <Search24Regular style={{ fontSize: '40px' }} />
                                    </div>
                                    <h1 className={styles.appNameTitle}>Lupa IA</h1>
                                </div> */}
                                <h2 className={styles.chatEmptyStateTitle}>
                                    {selectedSourcesCount === 0
                                        ? "Formula tu pregunta"
                                        : selectedSourcesCount === 1
                                            ? "¿Qué deseas saber de la publicación seleccionada?"
                                            : (
                                                <>
                                                    ¿Qué deseas saber de las <span className={styles.highlightedCount}>{selectedSourcesCount}</span> publicaciones seleccionadas?
                                                </>
                                            )
                                    }
                                </h2>
                            </div>
                        )}
                        {lastQuestionRef.current && (
                            <div className={styles.chatMessageStream}>
                                {answers.map((answer, index) => (
                                    <div key={index}>
                                        <UserChatMessage message={answer[0]} />
                                        <div className={styles.chatMessageGpt}>
                                            <Answer
                                                key={index}
                                                answer={answer[1]}
                                                isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                                onCitationClicked={(c, n) => onShowCitation(c, n, index)}
                                                onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                onFollowupQuestionClicked={q => makeApiRequestGpt(q)}
                                                showFollowupQuestions={false}
                                                showSources={true}
                                            />
                                        </div>
                                    </div>
                                ))}
                                {isLoading && (
                                    <>
                                        <UserChatMessage message={lastQuestionRef.current} />
                                        <div className={styles.chatMessageGptMinWidth}>
                                            <AnswerLoading />
                                        </div>
                                    </>
                                )}
                                {error ? (
                                    <>
                                        <UserChatMessage message={lastQuestionRef.current} />
                                        <div className={styles.chatMessageGptMinWidth}>
                                            <AnswerError
                                                error={
                                                    error.toString() === "SyntaxError: Unexpected end of JSON input"
                                                        ? error_message_text + "Error: Orchestrator call failed or did not return a valid response."
                                                        : error_message_text + error.toString()
                                                }
                                                onRetry={() => makeApiRequestGpt(lastQuestionRef.current)}
                                            />
                                        </div>
                                    </>
                                ) : null}
                                <div ref={chatMessageStreamEnd} />
                            </div>
                        )}

                        {!lastQuestionRef.current && selectedSourcesCount === 0 && (
                            <SamplePrompts onPromptClick={(prompt) => makeApiRequestGpt(prompt)} isMobile={isMobile} />
                        )}

                        <div className={styles.chatInput}>
                            <QuestionInput clearOnSend placeholder={placeholderText} disabled={isLoading} onSend={question => makeApiRequestGpt(question)} />
                        </div>
                        <div className={styles.disclaimerText}>
                            La inteligencia artificial puede cometer errores. Considera verificar la información importante.
                        </div>
                    </div>
                </div>


            </div>

            {/* Modal de análisis para todas las pantallas */}
            {answers.length > 0 && activeAnalysisPanelTab && showAnalysisModal && (
                <div className={styles.modalOverlay} onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowAnalysisModal(false);
                        setActiveAnalysisPanelTab(undefined);
                    }
                }}>
                    <div className={styles.analysisModalContainer}>
                        <div className={styles.modalHeader}>
                            <h3>Referencias en documentos - Textos considerados</h3>
                            <button
                                className={styles.modalCloseButton}
                                onClick={() => {
                                    setShowAnalysisModal(false);
                                    setActiveAnalysisPanelTab(undefined);
                                }}
                                aria-label="Cerrar"
                            >
                                <Dismiss24Regular />
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <AnalysisPanel
                                className={styles.chatAnalysisPanel}
                                activeCitation={activeCitation}
                                onActiveTabChanged={(x: AnalysisPanelTabs) => onToggleTab(x, selectedAnswer)}
                                citationHeight="600px"
                                answer={answers[selectedAnswer][1]}
                                activeTab={activeAnalysisPanelTab}
                                fileType={fileType}
                                isMobile={isMobile}
                            />
                        </div>
                    </div>
                </div>
            )}


            <Panel
                headerText="Configure answer generation"
                isOpen={isConfigPanelOpen}
                isBlocking={false}
                onDismiss={() => setIsConfigPanelOpen(false)}
                closeButtonAriaLabel="Close"
                onRenderFooterContent={() => <DefaultButton onClick={() => setIsConfigPanelOpen(false)}>Close</DefaultButton>}
                isFooterAtBottom={true}
            >
                <TextField
                    className={styles.chatSettingsSeparator}
                    defaultValue={promptTemplate}
                    label="Override prompt template"
                    multiline
                    autoAdjustHeight
                    onChange={onPromptTemplateChange}
                />

                <SpinButton
                    className={styles.chatSettingsSeparator}
                    label="Retrieve this many documents from search:"
                    min={1}
                    max={50}
                    defaultValue={retrieveCount.toString()}
                    onChange={onRetrieveCountChange}
                />
                <TextField className={styles.chatSettingsSeparator} label="Exclude category" onChange={onExcludeCategoryChanged} />
                <Checkbox
                    className={styles.chatSettingsSeparator}
                    checked={useSemanticRanker}
                    label="Use semantic ranker for retrieval"
                    onChange={onUseSemanticRankerChange}
                />
                <Checkbox
                    className={styles.chatSettingsSeparator}
                    checked={useSemanticCaptions}
                    label="Use query-contextual summaries instead of whole documents"
                    onChange={onUseSemanticCaptionsChange}
                    disabled={!useSemanticRanker}
                />
                <Checkbox
                    className={styles.chatSettingsSeparator}
                    checked={useSuggestFollowupQuestions}
                    label="Suggest follow-up questions"
                    onChange={onUseSuggestFollowupQuestionsChange}
                />
            </Panel>
        </div>
    );
};

export default Chat;
